'use client';

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { Socket } from 'socket.io-client';
import { useCurrentUser } from '../auth/hooks/useCurrentUser';
import { useGlobalConversationEvents } from '@/features/messaging';
import { useSocketConnection } from './socket/useSocketConnection';
import { useSocketGlobalHandlers } from './socket/useSocketGlobalHandlers';
import { PresenceInitializer } from './socket/PresenceInitializer';

// Load debug harness in development (attaches to window.__socketDebug)
if (process.env.NODE_ENV === 'development') {
  import('../debug/socketDebugHarness').catch(() => {
    // Silently ignore if debug harness fails to load
  });
}

interface SocketContextType {
  socket: Socket | null;
  connected: boolean;
  connecting: boolean;
  /** True once the socket has been connected for >= 2 seconds (debounced).
   *  Consumers can check this before triggering refetches to avoid request storms. */
  socketStable: boolean;
  emit: (event: string, data?: unknown) => void;
  joinRoom: (roomName: string, data?: unknown) => void;
  leaveRoom: (roomName: string, data?: unknown) => void;
  on: (event: string, callback: (...args: unknown[]) => void) => void;
  off: (event: string, callback?: (...args: unknown[]) => void) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

// Global conversation events handler component
const GlobalConversationEventsHandler: React.FC = () => {
  useGlobalConversationEvents();
  return null;
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { apiUserId } = useCurrentUser();
  const { socket, connected, connecting, socketStable, socketClient, registerHandler } = useSocketConnection();

  // Register global event handlers (message_created, conversation_updated, etc.)
  useSocketGlobalHandlers(socket, registerHandler);

  // Wrapper functions that check connection state
  const emit = useCallback((event: string, data?: unknown) => {
    if (typeof window === 'undefined') return;
    if (connected && socket) {
      socketClient.emit(event, data);
    } else {
      reduxLogger.warn(`[SocketProvider] Cannot emit '${event}' - socket not connected`);
    }
  }, [connected, socket, socketClient]);

  const joinRoom = useCallback((roomName: string, data?: unknown) => {
    if (typeof window === 'undefined') return;
    if (connected && socket) {
      socketClient.joinRoom(roomName, data);
    } else {
      reduxLogger.warn(`[SocketProvider] Cannot join room '${roomName}' - socket not connected`);
    }
  }, [connected, socket, socketClient]);

  const leaveRoom = useCallback((roomName: string, data?: unknown) => {
    if (typeof window === 'undefined') return;
    if (connected && socket) {
      socketClient.leaveRoom(roomName, data);
    } else {
      reduxLogger.warn(`[SocketProvider] Cannot leave room '${roomName}' - socket not connected`);
    }
  }, [connected, socket, socketClient]);

  const on = useCallback((event: string, callback: (...args: unknown[]) => void) => {
    if (typeof window === 'undefined') return;
    if (socket) {
      socketClient.on(event, callback);
    }
  }, [socket, socketClient]);

  const off = useCallback((event: string, callback?: (...args: unknown[]) => void) => {
    if (typeof window === 'undefined') return;
    if (socket) {
      socketClient.off(event, callback);
    }
  }, [socket, socketClient]);

  const contextValue = useMemo<SocketContextType>(() => ({
    socket,
    connected,
    connecting,
    socketStable,
    emit,
    joinRoom,
    leaveRoom,
    on,
    off
  }), [socket, connected, connecting, socketStable, emit, joinRoom, leaveRoom, on, off]);

  return (
    <SocketContext.Provider value={contextValue}>
      <PresenceInitializer connected={connected} currentUserId={apiUserId || undefined} />
      <GlobalConversationEventsHandler />
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

/** Safe version — returns null when outside SocketProvider (e.g., non-dashboard pages). */
export const useSocketSafe = (): SocketContextType | null => {
  return useContext(SocketContext) ?? null;
};
